import { useMemo, useState } from 'react';
import { Segmented } from '../components/data';
import { Empty, ErrorBanner, Field, Loading, Modal, PageHead } from '../components/ui';
import { api } from '../lib/api';
import { downloadCsv, fileStamp } from '../lib/download';
import { inBook } from '../lib/amBook';
import { formatCompactCurrency, formatDate, formatNumber, toLocalDateString } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { AmClientRow, AmScheduleRow, AmSummary, Me } from '../lib/types';
import { AccountManagerPicker } from './am/AccountManagerPicker';
import { AmBusinessCases } from './am/AmBusinessCase';
import { AmClientImport } from './am/AmClientImport';
import { AmClientWorkspace } from './am/AmClientWorkspace';
import { AmOverview } from './am/AmOverview';
import { AmMatrixExportModal } from './am/AmProductMatrix';
import { AmPipeline } from './am/AmPipeline';
import { AmQuestions } from './am/AmQuestions';
import { AmRecTemplates } from './am/AmRecTemplates';
import { AmSops } from './am/AmSops';
import { AmSow } from './am/AmSow';
import { StateBadge, Tabs } from './am/amUi';

type Tab = 'clients' | 'schedule' | 'pipeline' | 'businessCase' | 'questions' | 'recTemplates' | 'sops' | 'sow';

const TABS: { key: Tab; label: string; icon?: string }[] = [
  { key: 'clients', label: 'Clients', icon: '☺' },
  { key: 'schedule', label: 'Review schedule', icon: '▤' },
  { key: 'pipeline', label: 'Pipeline', icon: '◇' },
  /* Today's contracts against what we propose — the comparison spreadsheet, as a page. */
  { key: 'businessCase', label: 'Business Case comparison', icon: '⇄' },
  { key: 'questions', label: 'Audit templates', icon: '◫' },
  /* Saved from the Add recommendation form; an admin edits or deletes them. */
  { key: 'recTemplates', label: 'Recommendation templates', icon: '◇' },
  { key: 'sops', label: 'SOPs', icon: '❋' },
  /* The task library is inside this tab, beside the scopes it is built into:
     account management's own list of what it sells, kept by the people
     writing the scopes rather than by an administrator. */
  { key: 'sow', label: 'Scope of work', icon: '§' },
];

const CLIENT_STATUSES = ['Client', 'Prospect', 'At Risk', 'Inactive', 'Churned'] as const;
const UNASSIGNED = '__none__';

/**
 * Account management.
 *
 * The whole AM platform, in this app's design system: clients, the people on
 * them, the review schedule, the environment audits, the costed recommendations
 * those audits produced, the cross-client pipeline of that work, the audit
 * question bank, the SOP library and the scope-of-work builder.
 *
 * The reviews are the point. A client nobody has sat down with is a client
 * somebody else is talking to — so the summary leads with what is overdue, and
 * with the clients that have nothing booked at all and therefore can never
 * appear overdue.
 */
export function AccountManagementPage() {
  const summary = useResource<AmSummary>('/am/summary');
  /* The overview's dials are worked out from the client list so that each one
     opens onto exactly the clients it counted. */
  const overviewClients = useResource<AmClientRow[]>('/am/clients');
  const [tab, setTab] = useState<Tab>('clients');
  const [clientId, setClientId] = useState<string | null>(null);
  /* The client list's account-manager filter, held here rather than in the
     list: opening a client unmounts the list, and a filter kept inside it was
     gone by the time anybody came back. Held here, the client page can offer
     to go back to that manager's clients. */
  const [manager, setManager] = useState('');

  const s = summary.data;
  const organizationName = s?.OrganizationName ?? 'Our company';
  const reload = () => {
    summary.reload();
    overviewClients.reload();
  };

  if (clientId) {
    return (
      <AmClientWorkspace
        clientId={clientId}
        organizationName={organizationName}
        onBack={() => {
          /* "All clients" means all of them: the manager filter goes. */
          setManager('');
          setClientId(null);
          reload();
        }}
        backToManager={
          manager
            ? {
                label: manager === UNASSIGNED ? 'Unassigned clients' : `${manager}'s clients`,
                onClick: () => {
                  setClientId(null);
                  reload();
                },
              }
            : undefined
        }
        onDeleted={() => {
          setClientId(null);
          reload();
        }}
      />
    );
  }

  const openClient = (id: string) => {
    setClientId(id);
    window.scrollTo({ top: 0 });
  };

  return (
    <>
      <PageHead
        title="Account management"
        lede="Every client, who is on the account, when they were last reviewed, and what the environment audits found. The reviews are the point: a client nobody has sat down with is a client somebody else is talking to."
      />

      {/* The tabs first, so moving between them never means scrolling past
          the dashboard. */}
      <Tabs<Tab> tabs={TABS} active={tab} onChange={setTab} prominent />

      {summary.error && <ErrorBanner message={summary.error} />}

      {s && overviewClients.data && (
        <AmOverview
          summary={s}
          clients={overviewClients.data}
          onOpenClient={openClient}
          onOpenPipeline={() => setTab('pipeline')}
        />
      )}

      {tab === 'clients' && (
        <ClientList onOpen={openClient} onChanged={reload} manager={manager} onManager={setManager} />
      )}
      {tab === 'schedule' && <ScheduleView onOpen={openClient} />}
      {tab === 'pipeline' && <AmPipeline onOpenClient={openClient} />}
      {tab === 'businessCase' && <AmBusinessCases organizationName={organizationName} />}
      {tab === 'questions' && <AmQuestions />}
      {tab === 'recTemplates' && <AmRecTemplates />}
      {tab === 'sops' && <AmSops />}
      {tab === 'sow' && <AmSow organizationName={organizationName} />}
    </>
  );
}

/* =========================================================================
   Client list
   ========================================================================= */

function ClientList({
  onOpen,
  onChanged,
  manager,
  onManager: setManager,
}: {
  onOpen: (id: string) => void;
  onChanged: () => void;
  /** The account-manager filter — the page's, so it outlives opening a client. */
  manager: string;
  onManager: (m: string) => void;
}) {
  /* Deactivated clients are off the list unless an administrator asks. The
     API enforces that too — asking for them without the role gets the live
     list back, not a 403, because it is a filter and not a secret. */
  /* Read here rather than threaded down from App: the same call the Admin page
     makes, and `useResource` is cached per path by the browser's own request
     to /me being trivial. */
  const me = useResource<Me>('/me');
  const isAdmin = me.data?.appRole === 'admin';
  const [scope, setScope] = useState<'active' | 'inactive'>('active');
  const resource = useResource<AmClientRow[]>(
    scope === 'inactive' ? '/am/clients?scope=inactive' : '/am/clients',
  );
  const [filter, setFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  /* The spreadsheet import, separate from the Autotask one: a client
     list that was never in Autotask has to get in somehow. */
  const [importingFile, setImportingFile] = useState(false);
  const [matrix, setMatrix] = useState(false);
  const [reactivating, setReactivating] = useState<AmClientRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const all = resource.data ?? [];

  const managers = useMemo(
    () => [...new Set(all.map((c) => (c.AccountManager ?? '').trim()).filter(Boolean))].sort(),
    [all],
  );
  const hasUnassigned = all.some((c) => !(c.AccountManager ?? '').trim());

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return all.filter((c) => {
      const owner = (c.AccountManager ?? '').trim();
      if (manager === UNASSIGNED ? owner !== '' : manager && owner !== manager) return false;
      if (!q) return true;
      return [c.Name, c.Industry, c.AccountManager, c.PrimaryContact]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q));
    });
  }, [all, filter, manager]);

  /* Totals follow the filter, so picking an account manager shows that
     manager's book rather than the whole business. Prospects and churned
     clients stay in the list but not in the total (lib/amBook.ts). */
  const totalMrr = rows.reduce((sum, c) => sum + (inBook(c.Status) ? (c.MonthlyRevenue ?? 0) : 0), 0);

  function exportCsv() {
    const data: unknown[][] = [
      [
        'Client',
        'Account manager',
        'Industry',
        'Status',
        'Contract',
        'Monthly revenue',
        'Annual revenue',
        'Health',
        'Primary contact',
        'Email',
        'Phone',
        'Stakeholders',
        'Audits',
        'High or critical findings',
        'Open recommendations',
        'Products',
        'Next review',
        'Review overdue',
      ],
    ];
    for (const c of rows) {
      data.push([
        c.Name,
        c.AccountManager ?? 'Unassigned',
        c.Industry ?? '',
        c.Status ?? '',
        c.ContractType ?? '',
        c.MonthlyRevenue ?? 0,
        (c.MonthlyRevenue ?? 0) * 12,
        c.HealthScore ?? '',
        c.PrimaryContact ?? '',
        c.Email ?? '',
        c.Phone ?? '',
        c.Stakeholders,
        c.Audits,
        c.HighRisks,
        c.OpenRecommendations,
        c.Products,
        c.NextReviewDate?.slice(0, 10) ?? 'none booked',
        c.ReviewOverdue ? 'Yes' : 'No',
      ]);
    }
    downloadCsv(`clients-${fileStamp()}.csv`, data);
  }

  if (resource.loading) return <Loading label="Loading clients" />;
  if (resource.error) return <ErrorBanner message={resource.error} />;

  const reactivate = async (client: AmClientRow) => {
    setError(null);
    try {
      await api.post(`/am/clients/${client.AmClientId}/reactivate`, {});
      setReactivating(null);
      resource.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reactivate the client');
    }
  };

  return (
    <>
      {error && <ErrorBanner message={error} />}

      {isAdmin && (
        <div className="row mb-4">
          <Segmented
            label="Which clients"
            value={scope}
            onChange={setScope}
            options={[
              { id: 'active', label: 'Active' },
              { id: 'inactive', label: 'Deactivated' },
            ]}
          />
          {scope === 'inactive' && (
            <span className="xs muted">
              Off every list in the app — including the compliance subject picker and the product
              matrix — until they are put back.
            </span>
          )}
        </div>
      )}

      <div className="row-between wrap mb-4">
        <div className="row wrap">
          <input
            className="input"
            style={{ maxWidth: 260 }}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter clients…"
            aria-label="Filter clients"
          />
          <select
            className="select"
            style={{ width: 'auto' }}
            value={manager}
            onChange={(e) => setManager(e.target.value)}
            aria-label="Account manager"
          >
            <option value="">All account managers</option>
            {managers.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
            {hasUnassigned && <option value={UNASSIGNED}>Unassigned</option>}
          </select>
          <span className="xs muted">
            {rows.length} of {all.length} · {formatNumber(totalMrr, 'currency')}/mo ·{' '}
            {formatNumber(totalMrr * 12, 'currency')}/yr
          </span>
        </div>
        <div className="row wrap">
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMatrix(true)}>
            Product matrix
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={rows.length === 0}
            onClick={exportCsv}
          >
            Export CSV
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setImporting(true)}>
            Import from Autotask
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setImportingFile(true)}
          >
            Import a spreadsheet
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
            New client
          </button>
        </div>
      </div>

      {all.length === 0 ? (
        <Empty title={scope === 'inactive' ? 'Nobody has been deactivated' : 'No clients yet'}>
          <p>
            {scope === 'inactive'
              ? 'Every client is live. Deactivating one is done from their own page, and puts them here.'
              : 'Add the first client to start tracking reviews, audits and recommendations.'}
          </p>
        </Empty>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th style={{ width: 130 }}>Manager</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th className="num" style={{ width: 90 }}>MRR</th>
                  <th className="num" style={{ width: 70 }}>Health</th>
                  <th className="num" style={{ width: 65 }}>Audits</th>
                  <th className="num" style={{ width: 85 }}>High risk</th>
                  <th className="num" style={{ width: 75 }}>Open recs</th>
                  <th style={{ width: 130 }}>Next review</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.AmClientId}>
                    <td>
                      <div className="strong">{c.Name}</div>
                      <div className="xs muted">
                        {[c.Industry, c.PrimaryContact].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </td>
                    <td className="xs">
                      {c.AccountManager ?? <span className="subtle">Unassigned</span>}
                    </td>
                    <td>
                      <StateBadge status={c.Status} />
                    </td>
                    <td className="num">
                      {c.MonthlyRevenue ? formatCompactCurrency(c.MonthlyRevenue) : '—'}
                    </td>
                    <td className="num">
                      <span className={c.HealthScore !== null && c.HealthScore < 60 ? 'accent' : ''}>
                        {c.HealthScore ?? '—'}
                      </span>
                    </td>
                    <td className="num">{c.Audits || '—'}</td>
                    <td className="num">
                      {c.HighRisks > 0 ? <span className="accent">{c.HighRisks}</span> : '—'}
                    </td>
                    <td className="num">{c.OpenRecommendations || '—'}</td>
                    <td className="xs mono">
                      {c.NextReviewDate === null ? (
                        <span className="subtle">none booked</span>
                      ) : c.ReviewOverdue ? (
                        <span className="badge badge-critical">{formatDate(c.NextReviewDate)}</span>
                      ) : (
                        <>
                          {formatDate(c.NextReviewDate)}
                          {c.NextReviewCadence && (
                            <div className="xs muted">{c.NextReviewCadence}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="actions">
                      {scope === 'inactive' ? (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setReactivating(c)}
                        >
                          Reactivate
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => onOpen(c.AmClientId)}
                        >
                          Open
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reactivating && (
        <Modal
          title={`Reactivate ${reactivating.Name}?`}
          onClose={() => setReactivating(null)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setReactivating(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void reactivate(reactivating)}
              >
                Reactivate
              </button>
            </>
          }
        >
          <p className="small">
            They go back on every list, and whatever was switched off when they were deactivated is
            switched back on — the sales account, any compliance assessments that were archived at
            the time, an onboarding that was cancelled.
          </p>
          <p className="small muted mt-2">
            Only what THAT deactivation changed. An assessment somebody archived for their own
            reasons before then stays archived.
          </p>
        </Modal>
      )}

      {matrix && <AmMatrixExportModal onClose={() => setMatrix(false)} />}

      {importing && (
        <ImportFromAutotaskModal
          onClose={() => setImporting(false)}
          onImported={() => {
            resource.reload();
            onChanged();
          }}
        />
      )}

      {importingFile && (
        <AmClientImport
          onClose={() => setImportingFile(false)}
          onImported={() => {
            resource.reload();
            onChanged();
          }}
        />
      )}

      {creating && (
        <NewClientModal
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            resource.reload();
            onChanged();
            onOpen(id);
          }}
        />
      )}
    </>
  );
}

function NewClientModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (clientId: string) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    industry: '',
    accountManagerEmployeeId: null as number | null,
    primaryContact: '',
    email: '',
    phone: '',
    status: 'Client',
    monthlyRevenue: 0,
    contractType: '',
    healthScore: 75,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      const created = await api.post<{ AmClientId: string }>('/am/clients', {
        ...form,
        name: form.name.trim(),
      });
      onCreated(created.AmClientId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the client');
      setSaving(false);
    }
  }

  return (
    <Modal
      title="New client"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !form.name.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Creating…' : 'Create client'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <div className="form-grid">
        <Field label="Client name">
          <input
            className="input"
            autoFocus
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label="Industry">
          <input
            className="input"
            value={form.industry}
            onChange={(e) => setForm({ ...form, industry: e.target.value })}
          />
        </Field>
        <Field label="Account manager" hint="The pipeline is grouped by this.">
          <AccountManagerPicker
            value={form.accountManagerEmployeeId}
            onChange={(accountManagerEmployeeId) => setForm({ ...form, accountManagerEmployeeId })}
          />
        </Field>
        <Field label="Primary contact">
          <input
            className="input"
            value={form.primaryContact}
            onChange={(e) => setForm({ ...form, primaryContact: e.target.value })}
          />
        </Field>
        <Field label="Email">
          <input
            className="input"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field label="Phone">
          <input
            className="input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </Field>
        <Field label="Status">
          <select
            className="select"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            {CLIENT_STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </Field>
        <Field label="Monthly recurring revenue">
          <input
            className="input"
            type="number"
            min="0"
            value={form.monthlyRevenue}
            onChange={(e) => setForm({ ...form, monthlyRevenue: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="Contract type">
          <input
            className="input"
            placeholder="e.g. Managed services"
            value={form.contractType}
            onChange={(e) => setForm({ ...form, contractType: e.target.value })}
          />
        </Field>
        <Field label="Health score" hint="0-100. Shown until the first nightly calculation replaces it.">
          <input
            className="input"
            type="number"
            min="0"
            max="100"
            value={form.healthScore}
            onChange={(e) => setForm({ ...form, healthScore: Number(e.target.value) || 0 })}
          />
        </Field>
      </div>
      <Field label="Notes">
        <textarea
          className="textarea"
          rows={3}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </Field>
    </Modal>
  );
}

/* =========================================================================
   Review schedule
   ========================================================================= */

function ScheduleView({ onOpen }: { onOpen: (id: string) => void }) {
  const resource = useResource<AmScheduleRow[]>('/am/schedule');
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const [manager, setManager] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /* Held: it stops showing as overdue (migration 166). */
  const setComplete = async (id: string, complete: boolean) => {
    setProblem(null);
    try {
      await api.put(`/am/sbrs/${id}/complete`, { complete });
      resource.reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not update the review');
    }
  };

  const all = resource.data ?? [];

  const managers = useMemo(
    () => [...new Set(all.map((r) => (r.AccountManager ?? '').trim()).filter(Boolean))].sort(),
    [all],
  );
  const hasUnassigned = all.some((r) => !(r.AccountManager ?? '').trim());

  /* The manager filter narrows everything below it — the overdue count, the
     calendar, the list and the export — so picking someone shows their book. */
  const managed = useMemo(
    () =>
      all.filter((r) => {
        const owner = (r.AccountManager ?? '').trim();
        return manager === UNASSIGNED ? owner === '' : !manager || owner === manager;
      }),
    [all, manager],
  );
  const rows = overdueOnly ? managed.filter((r) => r.IsOverdue) : managed;

  if (resource.loading) return <Loading label="Loading the schedule" />;
  if (resource.error) return <ErrorBanner message={resource.error} />;

  if (all.length === 0) {
    return (
      <Empty title="Nothing scheduled">
        <p>No business review is booked against any client.</p>
      </Empty>
    );
  }

  function exportCsv() {
    const data: unknown[][] = [
      [
        'Due',
        'Days away',
        'Overdue',
        'Client',
        'Account manager',
        'Review',
        'Cadence',
        'Health',
        'Monthly revenue',
        'Agenda',
      ],
    ];
    for (const r of rows) {
      data.push([
        r.NextDate?.slice(0, 10) ?? '',
        r.DaysAway ?? '',
        r.IsOverdue ? 'Yes' : 'No',
        r.ClientName,
        r.AccountManager ?? 'Unassigned',
        r.Title ?? '',
        r.Cadence ?? '',
        r.HealthScore ?? '',
        r.MonthlyRevenue ?? 0,
        r.Agenda ?? '',
      ]);
    }
    downloadCsv(`review-schedule-${fileStamp()}.csv`, data);
  }

  return (
    <>
      <div className="row-between wrap mb-4">
        <div className="row wrap">
          <Segmented
            label="Schedule view"
            value={view}
            onChange={setView}
            options={[
              { id: 'calendar', label: 'Calendar' },
              { id: 'list', label: 'List' },
            ]}
          />
          <select
            className="select"
            style={{ width: 'auto' }}
            value={manager}
            onChange={(e) => setManager(e.target.value)}
            aria-label="Account manager"
          >
            <option value="">All account managers</option>
            {managers.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
            {hasUnassigned && <option value={UNASSIGNED}>Unassigned</option>}
          </select>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(e) => setOverdueOnly(e.target.checked)}
            />
            Overdue only ({managed.filter((r) => r.IsOverdue).length})
          </label>
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={rows.length === 0}
          onClick={exportCsv}
        >
          Export CSV
        </button>
      </div>

      {problem && <ErrorBanner message={problem} />}

      {rows.length === 0 ? (
        <Empty title="Nothing to show">
          <p>No review matches this account manager{overdueOnly ? ' that is overdue' : ''}.</p>
        </Empty>
      ) : view === 'calendar' ? (
        <ScheduleCalendar rows={rows} showManager={!manager} onOpen={onOpen} />
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Due</th>
                  <th>Client</th>
                  <th style={{ width: 130 }}>Manager</th>
                  <th>Review</th>
                  <th style={{ width: 100 }}>Cadence</th>
                  <th className="num" style={{ width: 80 }}>Health</th>
                  <th className="num" style={{ width: 90 }}>MRR</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.AmSbrId}>
                    <td className="mono xs">
                      {r.CompletedUtc ? (
                        <>
                          {r.NextDate && formatDate(r.NextDate)}
                          <div>
                            <span
                              className="badge badge-good"
                              title={`Completed ${formatDate(r.CompletedUtc)}${r.CompletedBy ? ` by ${r.CompletedBy}` : ''}`}
                            >
                              Completed
                            </span>
                          </div>
                        </>
                      ) : r.NextDate === null ? (
                        <span className="subtle">no date</span>
                      ) : r.IsOverdue ? (
                        <>
                          <span className="badge badge-critical">{formatDate(r.NextDate)}</span>
                          {r.DaysAway !== null && (
                            <div className="xs muted">{Math.abs(r.DaysAway)} days late</div>
                          )}
                        </>
                      ) : (
                        <>
                          {formatDate(r.NextDate)}
                          {r.DaysAway !== null && r.DaysAway <= 30 && (
                            <div className="xs muted">in {r.DaysAway} days</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="strong">{r.ClientName}</td>
                    <td className="xs">
                      {r.AccountManager ?? <span className="subtle">Unassigned</span>}
                    </td>
                    <td>
                      <div>{r.Title ?? '—'}</div>
                      {r.Agenda && <div className="xs muted">{r.Agenda}</div>}
                    </td>
                    <td className="xs">{r.Cadence ?? '—'}</td>
                    <td className="num">
                      <span className={r.HealthScore !== null && r.HealthScore < 60 ? 'accent' : ''}>
                        {r.HealthScore ?? '—'}
                      </span>
                    </td>
                    <td className="num">
                      {r.MonthlyRevenue ? formatCompactCurrency(r.MonthlyRevenue) : '—'}
                    </td>
                    <td className="actions">
                      {r.CompletedUtc ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          title="Not held after all — it counts as due (or overdue) again"
                          onClick={() => void setComplete(r.AmSbrId, false)}
                        >
                          Reopen
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          title="The review was held — it stops showing as overdue"
                          onClick={() => void setComplete(r.AmSbrId, true)}
                        >
                          ✓ Mark complete
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => onOpen(r.AmClientId)}
                      >
                        Open client
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * The schedule as a month. A review sits on its NextDate; one with no date
 * cannot be placed, so it is counted under the grid rather than dropped.
 * Overdue reviews keep their own month — the count beside the month name says
 * how many are behind you, so moving forward never hides them.
 */
function ScheduleCalendar({
  rows,
  showManager,
  onOpen,
}: {
  rows: AmScheduleRow[];
  showManager: boolean;
  onOpen: (id: string) => void;
}) {
  const today = toLocalDateString(new Date());
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const byDay = useMemo(() => {
    const map = new Map<string, AmScheduleRow[]>();
    for (const r of rows) {
      if (!r.NextDate) continue;
      const key = r.NextDate.slice(0, 10);
      const list = map.get(key);
      if (list) list.push(r);
      else map.set(key, [r]);
    }
    for (const list of map.values()) list.sort((a, b) => a.ClientName.localeCompare(b.ClientName));
    return map;
  }, [rows]);

  /* Whole weeks, Sunday first, covering the month. */
  const days = useMemo(() => {
    const y = month.getFullYear();
    const m = month.getMonth();
    const lead = month.getDay();
    const count = Math.ceil((lead + new Date(y, m + 1, 0).getDate()) / 7) * 7;
    return Array.from({ length: count }, (_, i) => new Date(y, m, 1 - lead + i));
  }, [month]);

  const monthKey = toLocalDateString(month).slice(0, 7);
  const inMonth = rows.filter((r) => r.NextDate?.startsWith(monthKey)).length;
  const earlier = rows.filter((r) => r.NextDate && r.NextDate.slice(0, 7) < monthKey).length;
  const later = rows.filter((r) => r.NextDate && r.NextDate.slice(0, 7) > monthKey).length;
  const undated = rows.filter((r) => !r.NextDate).length;

  const shift = (by: number) =>
    setMonth(new Date(month.getFullYear(), month.getMonth() + by, 1));

  return (
    <div className="card">
      <div className="am-cal-bar">
        <div className="row">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => shift(-1)}
            aria-label="Previous month"
          >
            ‹
          </button>
          <h3 className="am-cal-title">
            {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
          </h3>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => shift(1)}
            aria-label="Next month"
          >
            ›
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              const d = new Date();
              setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
            }}
          >
            Today
          </button>
        </div>
        <span className="xs muted">
          {inMonth} review{inMonth === 1 ? '' : 's'} this month
          {earlier > 0 && ` · ${earlier} earlier`}
          {later > 0 && ` · ${later} later`}
        </span>
      </div>

      <div className="am-cal-wrap">
        <div className="am-cal" role="grid" aria-label="Review calendar">
          {WEEKDAYS.map((d) => (
            <div key={d} className="am-cal-dow" role="columnheader">
              {d}
            </div>
          ))}
          {days.map((d) => {
            const key = toLocalDateString(d);
            const items = byDay.get(key) ?? [];
            const outside = d.getMonth() !== month.getMonth();
            return (
              <div
                key={key}
                role="gridcell"
                className={`am-cal-day${outside ? ' is-outside' : ''}${key === today ? ' is-today' : ''}`}
              >
                <div className="am-cal-date">{d.getDate()}</div>
                {items.map((r) => (
                  <button
                    key={r.AmSbrId}
                    type="button"
                    className={`am-cal-item${r.CompletedUtc ? ' is-done' : r.IsOverdue ? ' is-overdue' : ''}`}
                    onClick={() => onOpen(r.AmClientId)}
                    title={[
                      r.ClientName,
                      r.Title,
                      r.AccountManager ?? 'Unassigned',
                      r.CompletedUtc ? `Completed ${formatDate(r.CompletedUtc)}` : null,
                      r.IsOverdue && r.DaysAway !== null
                        ? `${Math.abs(r.DaysAway)} days late`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  >
                    <span className="am-cal-client">
                      {r.CompletedUtc && '✓ '}
                      {r.ClientName}
                    </span>
                    {showManager && (
                      <span className="am-cal-manager">{r.AccountManager ?? 'Unassigned'}</span>
                    )}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {undated > 0 && (
        <p className="xs muted am-cal-foot">
          {undated} review{undated === 1 ? ' has' : 's have'} no date and can't be placed — switch
          to List to see {undated === 1 ? 'it' : 'them'}.
        </p>
      )}
    </div>
  );
}

interface AutotaskCustomer {
  id: number;
  name: string;
  /** The company's account manager in Autotask (its owner resource). */
  accountManager: string | null;
  accountManagerEmail: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  /** linked: already here and linked · match: here under exactly this name,
   *  importing links it · new: importing creates it. */
  state: 'linked' | 'match' | 'new';
  amClientId: string | null;
}

interface ImportResult {
  created: { name: string; amClientId: string }[];
  linked: { name: string; amClientId: string }[];
  skipped: { name: string; reason: string }[];
}

/**
 * Bring clients in from Autotask's active customers, so the name here is
 * exactly the name in Autotask and the client arrives linked to its company
 * (the Technical Support slide and anything else that reads Autotask need no
 * matching). See routes/amAutotask.ts.
 */
function ImportFromAutotaskModal({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const list = useResource<AutotaskCustomer[]>('/am/autotask/customers');
  const [search, setSearch] = useState('');
  const [show, setShow] = useState<'todo' | 'all'>('todo');
  const [picked, setPicked] = useState<Set<number>>(new Set());
  /* WHO THE IMPORTED CLIENTS ARE ASSIGNED TO. On its own it filters nothing:
     picking somebody here used to narrow the list to the customers Autotask
     already has them against, which made "import these and give them to
     Barbara" impossible whenever Autotask disagreed — and silently emptied
     the list when it had never heard of them. Narrowing is the tick box
     below, and it is off unless asked for. */
  const [manager, setManager] = useState<number | null>(null);
  /* The person chosen, matched to Autotask's own account manager on each
     company by email, else by full name — only used when the list is being
     narrowed to them. */
  const [managerEmp, setManagerEmp] = useState<{ FullName: string; Email: string | null } | null>(null);
  const [onlyTheirs, setOnlyTheirs] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const all = list.data ?? [];
  const counts = useMemo(
    () => ({
      new: all.filter((c) => c.state === 'new').length,
      match: all.filter((c) => c.state === 'match').length,
      linked: all.filter((c) => c.state === 'linked').length,
    }),
    [all],
  );
  const managedBy = useMemo(() => {
    if (!managerEmp || !onlyTheirs) return null;
    const email = managerEmp.Email?.trim().toLowerCase() || null;
    const name = managerEmp.FullName.trim().toLowerCase();
    return (c: AutotaskCustomer) =>
      (email !== null && c.accountManagerEmail?.toLowerCase() === email) || (c.accountManager ?? '').trim().toLowerCase() === name;
  }, [managerEmp, onlyTheirs]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter(
      (c) => (show === 'all' || c.state !== 'linked') && (!managedBy || managedBy(c)) && (!q || c.name.toLowerCase().includes(q)),
    );
  }, [all, search, show, managedBy]);
  /* Counted as the list is shown: "not linked yet" leaves out the ones already here. */
  const managedCount = managedBy ? all.filter((c) => managedBy(c) && (show === 'all' || c.state !== 'linked')).length : 0;
  const managedHere = managedBy ? all.filter((c) => managedBy(c) && c.state === 'linked').length : 0;
  const pickable = shown.filter((c) => c.state !== 'linked');
  const allShownPicked = pickable.length > 0 && pickable.every((c) => picked.has(c.id));

  const toggle = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<ImportResult>('/am/autotask/import', {
        companyIds: [...picked],
        accountManagerEmployeeId: manager,
      });
      setResult(r);
      setPicked(new Set());
      list.reload();
      onImported();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not import from Autotask');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Import clients from Autotask"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {result ? 'Done' : 'Cancel'}
          </button>
          <button type="button" className="btn btn-primary" disabled={busy || picked.size === 0} onClick={() => void submit()}>
            {busy ? 'Importing…' : `Import ${picked.size || ''} client${picked.size === 1 ? '' : 's'}`.replace('  ', ' ')}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      {list.error && <ErrorBanner message={list.error} />}

      {result && (
        <div className="card mb-4">
          <div className="card-body tight small">
            <strong>
              {result.created.length} created
              {result.linked.length ? ` · ${result.linked.length} linked` : ''}
              {result.skipped.length ? ` · ${result.skipped.length} skipped` : ''}
            </strong>
            {result.skipped.length > 0 && (
              <ul className="xs muted mt-2">
                {result.skipped.map((x) => (
                  <li key={x.name}>
                    {x.name} — {x.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <p className="small muted mb-2">
        Autotask's active customers. A client imported here keeps exactly its Autotask name and is linked to its Autotask
        company. A client already here under the same name is linked and given Autotask's exact spelling, not duplicated.
      </p>

      {list.loading ? (
        <Loading label="Reading Autotask's customers" />
      ) : (
        list.data && (
          <>
            <div className="row wrap mb-2" style={{ alignItems: 'flex-end' }}>
              <Field label="Search">
                <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Company name" />
              </Field>
              <Field label="Show">
                <Segmented
                  label="Show"
                  options={[
                    { id: 'todo', label: `Not linked yet (${counts.new + counts.match})` },
                    { id: 'all', label: `All (${all.length})` },
                  ]}
                  value={show}
                  onChange={setShow}
                />
              </Field>
              <Field
                label="Assign account manager"
                hint="Everything imported now is given to them. Leave as Unassigned to decide later."
              >
                <AccountManagerPicker
                  value={manager}
                  onChange={setManager}
                  onPickEmployee={(e) => {
                    setManagerEmp(e);
                    /* Only when the list is being narrowed to them does the
                       selection have to go: the rows under it change. With
                       the list untouched, a selection already made stands. */
                    if (onlyTheirs) setPicked(new Set());
                  }}
                />
              </Field>
            </div>

            {managerEmp && (
              <label className="checkbox small mb-2">
                <input
                  type="checkbox"
                  checked={onlyTheirs}
                  onChange={(e) => {
                    setOnlyTheirs(e.target.checked);
                    /* The list is about to change under any selection. */
                    setPicked(new Set());
                  }}
                />
                Only show customers Autotask already has against {managerEmp.FullName}
              </label>
            )}

            {managerEmp && onlyTheirs && (
              <p className="xs muted mb-2">
                Showing the {managedCount} Autotask customer{managedCount === 1 ? '' : 's'} with {managerEmp.FullName} as account manager
                {show === 'todo' && managedHere > 0 ? ` not yet here (${managedHere} already are)` : ''}
                {managedCount === 0 && managedHere === 0 ? ' — none found by their email or name in Autotask' : ''}.
              </p>
            )}

            {managerEmp && !onlyTheirs && (
              <p className="xs muted mb-2">
                Every client imported now gets {managerEmp.FullName} as account manager here, whoever
                Autotask has against it.
              </p>
            )}

            <div className="row-between mb-2">
              <label className="checkbox small">
                <input
                  type="checkbox"
                  checked={allShownPicked}
                  disabled={pickable.length === 0}
                  onChange={() =>
                    setPicked((prev) => {
                      const next = new Set(prev);
                      for (const c of pickable) {
                        if (allShownPicked) next.delete(c.id);
                        else next.add(c.id);
                      }
                      return next;
                    })
                  }
                />
                Select all {pickable.length} shown
              </label>
              <span className="xs muted">{picked.size} selected</span>
            </div>

            <div className="am-import-list">
              {shown.length === 0 ? (
                <p className="muted small">
                  {all.length === 0 ? 'Autotask returned no active customers.' : 'Nothing matches — every customer may already be here.'}
                </p>
              ) : (
                shown.map((c) => (
                  <label key={c.id} className={`am-import-row${c.state === 'linked' ? ' is-done' : ''}`}>
                    <input
                      type="checkbox"
                      disabled={c.state === 'linked'}
                      checked={c.state === 'linked' || picked.has(c.id)}
                      onChange={() => toggle(c.id)}
                    />
                    <span className="grow">
                      <span className="strong small">{c.name}</span>
                      {(c.accountManager || c.phone || c.address) && (
                        <span className="xs muted">
                          {' · '}
                          {[c.accountManager && `AM ${c.accountManager}`, c.phone, c.address].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                    <span className="xs muted">
                      {c.state === 'linked' ? 'Already here' : c.state === 'match' ? 'Here by name — will link and match spelling' : 'New'}
                    </span>
                  </label>
                ))
              )}
            </div>
          </>
        )
      )}
    </Modal>
  );
}
